/**
 * @fileoverview Third-party script loader utility with built-in timeout and error handling.
 * 
 * This module provides a safe and reliable way to load third-party scripts dynamically,
 * with specific support for Vimeo player initialization. It includes timeout handling,
 * error recovery, and validation to ensure scripts load properly without blocking the
 * main thread. The loader is particularly useful for lazy-loading external dependencies
 * only when needed.
 * 
 * @module helpers/third-party-loader
 */

/**
 * Configuration options for loading third-party scripts.
 */
interface ThirdPartyScriptOptions {
  /** The URL of the script to load */
  src: string;
  /** Whether to load the script asynchronously (default: true) */
  async?: boolean;
  /** Whether to defer script execution (default: false) */
  defer?: boolean;
  /** Callback function to execute when the script loads successfully */
  onLoad?: () => void;
  /** Callback function to execute when the script fails to load */
  onError?: (error: Event) => void;
  /** Maximum time in milliseconds to wait for the script to load (default: 10000) */
  timeout?: number;
}

// Extend Window interface for Vimeo
declare global {
  interface Window {
    Vimeo?: {
      Player: new (element: HTMLIFrameElement) => unknown;
    };
  }
}

/**
 * Interface for the third-party loader utility object.
 */
interface ThirdPartyLoader {
  loadScript: (options: ThirdPartyScriptOptions) => Promise<void>;
  loadVimeoPlayer: (element: HTMLIFrameElement) => Promise<void>;
  isVimeoEmbed: (src: string) => boolean;
}

/**
 * Third-party script loader utility providing safe dynamic script loading.
 */
const thirdPartyLoader: ThirdPartyLoader = {
  /**
   * Safely loads a third-party script with timeout and error handling.
   * 
   * This method creates a script element dynamically and appends it to the document head.
   * It includes timeout protection to prevent indefinite waiting and proper cleanup of
   * event listeners to avoid memory leaks.
   * 
   * @param {ThirdPartyScriptOptions} options - Configuration options for script loading
   * @returns {Promise<void>} A promise that resolves when the script loads successfully
   * @throws {Error} Throws an error if the script fails to load or times out
   * 
   * @example
   * // Load a script with default settings
   * await thirdPartyLoader.loadScript({
   *   src: 'https://cdn.example.com/library.js'
   * });
   * 
   * @example
   * // Load with custom timeout and callbacks
   * await thirdPartyLoader.loadScript({
   *   src: 'https://maps.googleapis.com/maps/api/js',
   *   timeout: 20000, // 20 seconds
   *   onLoad: () => console.log('Google Maps loaded'),
   *   onError: (error) => console.error('Failed to load Google Maps', error)
   * });
   * 
   * @example
   * // Load a script synchronously with defer
   * await thirdPartyLoader.loadScript({
   *   src: 'https://cdn.example.com/analytics.js',
   *   async: false,
   *   defer: true
   * });
   */
  async loadScript(options: ThirdPartyScriptOptions): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = options.src;
      script.async = options.async ?? true;
      script.defer = options.defer ?? false;

      const timeout = options.timeout ?? 10000; // 10 second default timeout
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
      };

      const onLoad = () => {
        cleanup();
        options.onLoad?.();
        resolve();
      };

      const onError = (error: Event) => {
        cleanup();
        console.warn(`Failed to load third-party script: ${options.src}`, error);
        options.onError?.(error);
        reject(new Error(`Script load timeout: ${options.src}`));
      };

      // Set timeout
      timeoutId = setTimeout(() => {
        cleanup();
        console.warn(`Script load timeout: ${options.src}`);
        reject(new Error(`Script load timeout: ${options.src}`));
      }, timeout);

      script.addEventListener('load', onLoad);
      script.addEventListener('error', onError);

      document.head.appendChild(script);
    });
  },

  /**
   * Checks if a given URL is a valid Vimeo embed URL.
   * 
   * This method validates URLs against known Vimeo embed patterns to ensure
   * that the Vimeo player API is only loaded for actual Vimeo content.
   * 
   * @param {string} src - The URL to validate
   * @returns {boolean} True if the URL is a valid Vimeo embed, false otherwise
   * 
   * @example
   * // Valid Vimeo URLs
   * thirdPartyLoader.isVimeoEmbed('https://player.vimeo.com/video/123456789'); // true
   * thirdPartyLoader.isVimeoEmbed('https://vimeo.com/123456789'); // true
   * 
   * @example
   * // Invalid URLs
   * thirdPartyLoader.isVimeoEmbed('https://youtube.com/watch?v=abc'); // false
   * thirdPartyLoader.isVimeoEmbed(''); // false
   */
  isVimeoEmbed(src: string): boolean {
    if (!src) return false;
    
    const vimeoPatterns = [
      /^https?:\/\/player\.vimeo\.com\/video\/\d+/,
      /^https?:\/\/vimeo\.com\/\d+/,
      /vimeo\.com.*\/embed\//
    ];
    
    return vimeoPatterns.some(pattern => pattern.test(src));
  },

  /**
   * Safely initializes a Vimeo player with validation and error handling.
   * 
   * This method first validates that the iframe contains a Vimeo embed, then
   * ensures the Vimeo Player API is loaded before initializing the player.
   * It includes proper error handling and adds an error class to the element
   * if initialization fails.
   * 
   * @param {HTMLIFrameElement} element - The iframe element containing the Vimeo embed
   * @returns {Promise<void>} A promise that resolves when the player is initialized
   * @throws {Error} Throws an error if the element is not a valid Vimeo embed or initialization fails
   * 
   * @example
   * // Initialize a Vimeo player
   * const iframe = document.querySelector('iframe[src*="vimeo.com"]');
   * try {
   *   await thirdPartyLoader.loadVimeoPlayer(iframe);
   *   console.log('Vimeo player initialized');
   * } catch (error) {
   *   console.error('Failed to initialize Vimeo player:', error);
   * }
   * 
   * @example
   * // Initialize multiple Vimeo players
   * const vimeoIframes = document.querySelectorAll('.vimeo-embed');
   * const promises = Array.from(vimeoIframes).map(iframe => 
   *   thirdPartyLoader.loadVimeoPlayer(iframe).catch(err => {
   *     console.error('Failed to load player for', iframe, err);
   *   })
   * );
   * await Promise.allSettled(promises);
   */
  async loadVimeoPlayer(element: HTMLIFrameElement): Promise<void> {
    try {
      // Validate that this is actually a Vimeo embed
      if (!this.isVimeoEmbed(element.src)) {
        throw new Error('Element is not a valid Vimeo embed');
      }

      // Check if Vimeo Player is available
      if (typeof window.Vimeo === 'undefined') {
        await this.loadScript({
          src: 'https://player.vimeo.com/api/player.js',
          timeout: 15000
        });
      }

      // Only initialize if we have the Vimeo Player constructor
      if (window.Vimeo?.Player) {
        new window.Vimeo.Player(element);
        return;
      } else {
        throw new Error('Vimeo Player not available');
      }
    } catch (error) {
      console.warn('Failed to initialize Vimeo player:', error);
      // Add error class to element for CSS styling
      element.classList.add('vimeo-error');
      throw error;
    }
  }
};

export default thirdPartyLoader; 
